class CartThresholdIndicator extends HTMLElement {
  constructor() {
    super();
    this.progressBar = this.querySelector('.cart-threshold-progress-fill');
    this.messageElement = this.querySelector('.cart-threshold-message');
    this.iconElements = this.querySelectorAll('.cart-threshold-icon');
    
    this.freeShippingThreshold = Number(window.freeShippingThreshold);
    this.thresholdOne = Number(window.thresholdOne);
    this.thresholdTwo = Number(window.thresholdTwo);
    
    this.maxThreshold = Math.max(
      this.freeShippingThreshold || 0,
      this.thresholdOne || 0,
      this.thresholdTwo || 0
    );
    
    this.currentWidth = 0;
    this.initialized = false;
    this.debounceTimeout = null;
    this.isAnimating = false;
    this.animationFrame = null;
    this.lastCartTotal = 0;
    this.targetWidth = 0;
    
    // Store icon positions globally to persist across DOM updates
    if (!window.cartThresholdIconPositions) {
      window.cartThresholdIconPositions = new Map();
    }
    
    console.log('Thresholds:', {
      freeShippingThreshold: this.freeShippingThreshold,
      thresholdOne: this.thresholdOne,
      thresholdTwo: this.thresholdTwo,
      maxThreshold: this.maxThreshold
    });
  }

  async connectedCallback() {
    await this.waitForThresholds();
    await this.initializeProgressBar();
    this.subscribeToCartUpdates();
    
    // Restore positions immediately when connected
    this.restoreIconPositions();
  }

  waitForThresholds(timeout = 300) {
    return new Promise((resolve) => {
      const check = () => {
        if (
          window.freeShippingThreshold > 0 ||
          window.thresholdOne > 0 ||
          window.thresholdTwo > 0
        ) return resolve();
        
        if (timeout <= 0) return resolve();
        
        setTimeout(() => {
          timeout -= 50;
          check();
        }, 50);
      };
      check();
    });
  }

  async initializeProgressBar() {
    if (this.initialized) return;

    try {
      const cartTotal = await this.getCartTotal();
      this.lastCartTotal = cartTotal;

      this.freeShippingThreshold = Number(window.freeShippingThreshold);
      this.thresholdOne = Number(window.thresholdOne);
      this.thresholdTwo = Number(window.thresholdTwo);

      this.maxThreshold = Math.max(
        this.freeShippingThreshold || 0,
        this.thresholdOne || 0,
        this.thresholdTwo || 0
      );

      if (!this.freeShippingThreshold && !this.thresholdOne && !this.thresholdTwo) {
        this.currentWidth = 0;
        this.targetWidth = 0;
        if (this.progressBar) this.progressBar.style.width = '0%';
        this.messageElement.textContent = '';
        this.iconElements.forEach(icon => icon.classList.remove('reached'));
        this.positionThresholdIcons();
        this.initialized = true;
        return;
      }

      this.targetWidth = Math.min(Math.round((cartTotal / this.maxThreshold) * 100), 100);
      this.currentWidth = this.targetWidth;
      if (this.progressBar) this.progressBar.style.width = `${this.currentWidth}%`;

      this.updateMessageAndIcons(cartTotal);
      this.positionThresholdIcons();
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing progress bar:', error);
    }
  }

  subscribeToCartUpdates() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      
      this.debounceTimeout = setTimeout(() => {
        this.updateProgressBar(event.cartData);
      }, 50);
    });
  }

  async getCartTotal() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const cartData = await response.json();
      console.log('Cart total fetched:', cartData.total_price);
      return cartData.total_price || 0;
    } catch (error) {
      console.error('Error fetching cart total:', error);
      return window.cartTotal || 0;
    }
  }

  animateProgressBar(targetWidth, duration = 500) {
    if (Math.abs(this.currentWidth - targetWidth) < 0.1) return;

    if (this.isAnimating) cancelAnimationFrame(this.animationFrame);
    
    this.isAnimating = true;
    const startWidth = this.currentWidth;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easedProgress = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      const newWidth = startWidth + (targetWidth - startWidth) * easedProgress;

      if (this.progressBar) {
        this.progressBar.classList.add('no-transition');
        this.progressBar.style.width = `${newWidth}%`;
      }

      this.currentWidth = newWidth;

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.isAnimating = false;
        this.currentWidth = targetWidth;
        if (this.progressBar) {
          this.progressBar.classList.remove('no-transition');
          this.progressBar.style.width = `${targetWidth}%`;
        }
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  positionThresholdIcons() {
    if (!this.maxThreshold || this.maxThreshold <= 0) return;

    // Re-query icon elements to ensure we have the latest DOM elements
    this.iconElements = this.querySelectorAll('.cart-threshold-icon');

    this.iconElements.forEach(icon => {
      const threshold = parseInt(icon.dataset.threshold);
      if (threshold && threshold > 0) {
        const percentage = Math.min((threshold / this.maxThreshold) * 100, 100);
        const leftPosition = percentage < 5 ? '0px' : 
                           percentage > 95 ? 'calc(100% - 44px)' : 
                           `calc(${percentage}% - 32px)`;
        
        icon.style.left = leftPosition;
        // Store position in global map
        window.cartThresholdIconPositions.set(threshold.toString(), leftPosition);
        console.log(`Positioned icon for threshold ${threshold} at ${leftPosition}`);
      }
    });
  }

  restoreIconPositions() {
    // Re-query icon elements
    this.iconElements = this.querySelectorAll('.cart-threshold-icon');
    
    this.iconElements.forEach(icon => {
      const threshold = icon.dataset.threshold;
      const cachedPosition = window.cartThresholdIconPositions.get(threshold);
      
      if (cachedPosition) {
        icon.style.left = cachedPosition;
        console.log(`Restored icon position for threshold ${threshold} to ${cachedPosition}`);
      } else {
        // If no cached position, calculate it
        const thresholdNum = parseInt(threshold);
        if (thresholdNum && thresholdNum > 0 && this.maxThreshold > 0) {
          const percentage = Math.min((thresholdNum / this.maxThreshold) * 100, 100);
          const leftPosition = percentage < 5 ? '0px' : 
                             percentage > 95 ? 'calc(100% - 64px)' : 
                             `calc(${percentage}% - 32px)`;
          
          icon.style.left = leftPosition;
          window.cartThresholdIconPositions.set(threshold, leftPosition);
          console.log(`Calculated and set icon position for threshold ${threshold} to ${leftPosition}`);
        }
      }
    });
  }

updateMessageAndIcons(cartTotal) {
  // re-grab the DOM nodes in case the markup was swapped out
  this.messageElement = this.querySelector('.cart-threshold-message');
  this.iconElements   = this.querySelectorAll('.cart-threshold-icon');

  if (!this.messageElement) {
    // nothing to update
    console.warn('No .cart-threshold-message found');
    return;
  }

  let message = '';
  const hasFreeShipping = this.freeShippingThreshold > 0;
  const hasThresholdOne = this.thresholdOne       > 0;
  const hasThresholdTwo = this.thresholdTwo       > 0 && this.thresholdTwo > this.thresholdOne;
  const isOneDollar = window.one_dollar === true || window.one_dollar === 'true' || window.giftPromo?.oneDollar === true || window.giftPromo?.oneDollar === 'true';
  const giftText = isOneDollar ? '$1 gift' : 'free gift';
  console.log('Cart threshold - window.one_dollar:', window.one_dollar, 'isOneDollar:', isOneDollar, 'giftText:', giftText);

  if (hasThresholdTwo && cartTotal >= this.thresholdTwo) {
    message = `Congratulations! You unlocked all ${giftText}s and free shipping!`;
  } else if (hasThresholdOne && cartTotal >= this.thresholdOne) {
    const amountLeft = hasThresholdTwo
      ? ((this.thresholdTwo - cartTotal) / 100).toFixed(2)
      : null;
    message = hasThresholdTwo
      ? `Congratulations! You unlocked 1 ${giftText} and free shipping, add $${amountLeft} for the second gift!`
      : `Congratulations! You unlocked a ${giftText} and free shipping!`;
  } else if (hasFreeShipping && cartTotal >= this.freeShippingThreshold) {
    const amountLeft = hasThresholdOne
      ? ((this.thresholdOne - cartTotal) / 100).toFixed(2)
      : null;
    message = hasThresholdOne
      ? `Free shipping unlocked! Add $${amountLeft} for a ${giftText}!`
      : 'Free shipping unlocked!';
  } else if (hasFreeShipping && cartTotal < this.freeShippingThreshold) {
    const amountLeft = ((this.freeShippingThreshold - cartTotal) / 100).toFixed(2);
    message = `Add $${amountLeft} to unlock free shipping!`;
  }

  // set reached classes on your icons
  this.iconElements.forEach(icon => {
    const threshold = parseInt(icon.dataset.threshold, 10);
    icon.classList.toggle('reached', threshold <= cartTotal);
  });

  // finally, update the message
  this.messageElement.textContent = message;
}
  async updateProgressBar(cartData) {
    if (!this.initialized) {
      await this.initializeProgressBar();
      return;
    }

    try {
      const cartTotal = cartData ? cartData.total_price : await this.getCartTotal();
      this.lastCartTotal = cartTotal;

      const newFreeShippingThreshold = Number(window.freeShippingThreshold);
      const newThresholdOne = Number(window.thresholdOne);
      const newThresholdTwo = Number(window.thresholdTwo);

      const newMaxThreshold = Math.max(
        newFreeShippingThreshold || 0,
        newThresholdOne || 0,
        newThresholdTwo || 0
      );

      if (
        newFreeShippingThreshold !== this.freeShippingThreshold ||
        newThresholdOne !== this.thresholdOne ||
        newThresholdTwo !== this.thresholdTwo ||
        newMaxThreshold !== this.maxThreshold
      ) {
        console.log('Thresholds changed, repositioning icons');
        this.freeShippingThreshold = newFreeShippingThreshold;
        this.thresholdOne = newThresholdOne;
        this.thresholdTwo = newThresholdTwo;
        this.maxThreshold = newMaxThreshold;
        this.positionThresholdIcons();
      } else {
        // Always restore positions after DOM updates
        this.restoreIconPositions();
      }

      if (!this.freeShippingThreshold && !this.thresholdOne && !this.thresholdTwo) {
        this.targetWidth = 0;
        this.animateProgressBar(0);
        this.messageElement.textContent = '';
        this.iconElements.forEach(icon => icon.classList.remove('reached'));
        return;
      }

      this.targetWidth = Math.min(Math.round((cartTotal / this.maxThreshold) * 100), 100);
      this.animateProgressBar(this.targetWidth);
      this.updateMessageAndIcons(cartTotal);
      
      // Ensure positions are restored after updating
      this.restoreIconPositions();
    } catch (error) {
      console.error('Error updating progress bar:', error);
    }
  }
}

// Global function to restore icon positions after DOM updates
window.restoreCartThresholdIconPositions = function() {
  const indicator = document.querySelector('cart-threshold-indicator');
  if (indicator && indicator.restoreIconPositions) {
    indicator.restoreIconPositions();
  }
};

customElements.define('cart-threshold-indicator', CartThresholdIndicator);
class GiftSliderManager {
  constructor() {
    this.thresholdOne = Number(window.thresholdOne) || 0;
    this.thresholdTwo = Number(window.thresholdTwo) || 0;
    this.cartTotal = 0;
    this.isUpdating = false;
    this.giftCount = 0;
    this.giftThresholdMap = new Map();
    this.debounceTimeout = null; // Add debounce timeout

    // Store original slider HTML for re-insertion
    this.originalSliderHTML = null;
    this.sliderParentElement = null;
    this.sliderNextSibling = null;

    // Auto-inject settings
    this.autoInject = window.autoInject === true || window.autoInject === 'true';
    this.autoInjectProductId = window.autoInjectProductId || null;
    this.autoInjectVariantId = window.autoInjectVariantId || null;
    this.isAutoInjecting = false; // Prevent multiple auto-inject attempts

    this.bindSelectButtons();
    this.subscribeToCartUpdates();
    this.checkAndRemoveGiftsOnLoad();
  }

  subscribeToCartUpdates() {
    
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'gift-slider-manager') return;
          if (event?.source === 'bundle-interaction' || event?.target?.closest('.buy_action, .product-grid-addtocart, .bundles_collectiond')) return;


      // IMMEDIATELY hide slider on any cart change for safety
      const slider = document.getElementById('GiftSliderContainer');

      if (slider) {
        slider.style.display = 'none';
      }
      
      // Add debounce to prevent rapid fire updates
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
      this.debounceTimeout = setTimeout(() => {
        this.refreshCartState(event.cartData);
      }, 100);
    });
  }

  async refreshCartState(cartData) {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      const data = cartData || await this.getCartData();
      this.cartTotal = data.total_price;
      this.giftCount = this.countGiftsInCart(data);

      console.log('Current gift count:', this.giftCount);
      this.updateGiftThresholdMap(data);

      // IMPORTANT: Remove invalid gifts FIRST, then update visibility
      await this.checkAndRemoveInvalidGifts(data);

      // Get updated data after gift cleanup
      let updatedData = await this.getCartData();
      const cartTotalWithoutGifts = await this.getCartTotalWithoutGifts();
      this.giftCount = this.countGiftsInCart(updatedData);

      // Handle auto-inject product (add or remove based on threshold)
      await this.checkAndHandleAutoInject(updatedData);

      // Re-fetch data after auto-inject operations
      updatedData = await this.getCartData();
      this.giftCount = this.countGiftsInCart(updatedData);

      // Only update slider visibility after cleanup is complete
      await this.updateSliderVisibility();

      if (window.cartThresholdIndicator) {
        window.cartThresholdIndicator.giftCount = this.giftCount;
        window.cartThresholdIndicator.updateProgressBar({
          total_price: cartTotalWithoutGifts,
          items: updatedData.items
        });
      }
    } catch (error) {
      console.error('Error refreshing cart state:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  countGiftsInCart(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    // Count only user-selected gifts (not auto-injected ones)
    return items.filter(item =>
      item.properties?._gift === 'true' &&
      item.properties?._auto_injected !== 'true'
    ).length;
  }

  countAutoInjectInCart(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    return items.filter(item => item.properties?._auto_injected === 'true').length;
  }

  hasAutoInjectInCart(cartData) {
    return this.countAutoInjectInCart(cartData) > 0;
  }

  getAutoInjectItem(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    return items.find(item => item.properties?._auto_injected === 'true');
  }

  async getCartTotalWithoutGifts() {
    try {
      const response = await fetch('/cart.js');
      const data = await response.json();
      let total = data.total_price;

      data.items.forEach(item => {
        // Exclude both regular gifts and auto-injected products from total
        if (item.properties?._gift === 'true' || item.properties?._auto_injected === 'true') {
          total -= item.final_line_price;
        }
      });

      return total;
    } catch (error) {
      console.error('Error calculating cart total without gifts:', error);
      return this.cartTotal;
    }
  }

  async checkAndHandleAutoInject(cartData) {
    // Skip if auto-inject is not enabled or no product configured
    if (!this.autoInject || !this.autoInjectVariantId) {
      return;
    }

    // Prevent concurrent auto-inject attempts
    if (this.isAutoInjecting) {
      console.log('Auto-inject already in progress, skipping');
      return;
    }

    const cartTotalWithoutGifts = await this.getCartTotalWithoutGifts();
    const hasAutoInject = this.hasAutoInjectInCart(cartData);

    console.log('Auto-inject check:', {
      autoInject: this.autoInject,
      autoInjectVariantId: this.autoInjectVariantId,
      cartTotalWithoutGifts,
      thresholdOne: this.thresholdOne,
      hasAutoInject,
      isAboveThreshold: cartTotalWithoutGifts >= this.thresholdOne
    });

    // If cart is above threshold and auto-inject product is not in cart, add it
    if (cartTotalWithoutGifts >= this.thresholdOne && !hasAutoInject) {
      console.log('Adding auto-inject product to cart');
      await this.addAutoInjectToCart();
    }
    // If cart drops below threshold and auto-inject product is in cart, remove it
    else if (cartTotalWithoutGifts < this.thresholdOne && hasAutoInject) {
      console.log('Removing auto-inject product from cart (below threshold)');
      const autoInjectItem = this.getAutoInjectItem(cartData);
      if (autoInjectItem) {
        await this.removeItemFromCart(autoInjectItem.key);
      }
    }
  }

  async addAutoInjectToCart() {
    if (this.isAutoInjecting) return;
    this.isAutoInjecting = true;

    try {
      console.log('Auto-injecting product:', this.autoInjectVariantId);

      const isOneDollar = window.one_dollar === true || window.one_dollar === 'true';
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            quantity: 1,
            id: this.autoInjectVariantId,
            properties: {
              '_auto_injected': 'true',
              '_gift': 'true',
              '_threshold': this.thresholdOne,
              '_one_dollar': isOneDollar ? 'true' : 'false'
            }
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to auto-inject product');

      const result = await response.json();
      console.log('Auto-inject product added successfully:', result);

      const updatedCartData = await this.getCartData();
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-slider-manager',
        cartData: updatedCartData
      });

      return updatedCartData;
    } catch (error) {
      console.error('Error auto-injecting product:', error);
    } finally {
      this.isAutoInjecting = false;
    }
  }

  updateGiftThresholdMap(cartData) {
    this.giftThresholdMap.clear();
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    
    items.forEach(item => {
      if (item.properties?._gift === 'true' && item.properties?._threshold) {
        this.giftThresholdMap.set(item.key, Number(item.properties._threshold));
      }
    });
    
    console.log('Updated giftThresholdMap:', Array.from(this.giftThresholdMap.entries()));
  }

  // SECURE: Actually remove/add slider from DOM
  async updateSliderVisibility() {
    // SAFETY: Hide all sliders by default on any change
    const allSliders = document.querySelectorAll('#GiftSliderContainer');
    allSliders.forEach(slider => {
      slider.style.display = 'none';
    });

    const slider = document.getElementById('GiftSliderContainer');

    // Store original HTML and position FIRST before any calculations
    if (slider && !this.originalSliderHTML) {
      this.originalSliderHTML = slider.outerHTML;
      this.sliderParentElement = slider.parentElement;
      this.sliderNextSibling = slider.nextElementSibling;
      console.log('Stored original slider HTML and position');
    }

    // If no slider exists and we never had one, this is auto-inject only mode - skip slider logic
    if (!slider && !this.originalSliderHTML) {
      console.log('No gift slider present (auto-inject only mode)');
      return;
    }

    // Always use fresh data to avoid stale calculations
    const cartData = await this.getCartData();
    const cartTotal = await this.getCartTotalWithoutGifts();
    this.giftCount = this.countGiftsInCart(cartData);
    
    const isSingleGiftPromo = this.thresholdTwo <= this.thresholdOne;
    
    // Determine if slider should be visible - using same logic as original
    let shouldShow = false;
    
    if (isSingleGiftPromo) {
      // Single gift promo: show only if eligible for first gift and don't have one yet
      shouldShow = this.thresholdOne > 0 && cartTotal >= this.thresholdOne && this.giftCount < 1;
    } else {
      // Two gift promo: show if eligible for first OR second gift
      const wantFirst = this.thresholdOne > 0 && cartTotal >= this.thresholdOne && this.giftCount < 1;
      const wantSecond = this.thresholdTwo > this.thresholdOne && cartTotal >= this.thresholdTwo && this.giftCount < 2;
      shouldShow = wantFirst || wantSecond;
    }

    const sliderCurrentlyExists = !!document.getElementById('GiftSliderContainer');

    // SECURE: Remove from DOM if shouldn't show but currently exists
    if (!shouldShow && sliderCurrentlyExists) {
      console.log('SECURITY: Removing gift slider from DOM - not eligible');
      slider.remove();
    } 
    // SECURE: Add back to DOM if should show but doesn't exist
    else if (shouldShow && !sliderCurrentlyExists && this.originalSliderHTML && this.sliderParentElement) {
      console.log('SECURITY: Adding gift slider back to DOM - now eligible');
      
      // Create new element from stored HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.originalSliderHTML;
      const newSlider = tempDiv.firstElementChild;
      
      // Remove any hidden attributes and EXPLICITLY show it
      newSlider.removeAttribute('hidden');
      newSlider.style.display = 'block';
      
      // Insert back in original position
      if (this.sliderNextSibling) {
        this.sliderParentElement.insertBefore(newSlider, this.sliderNextSibling);
      } else {
        this.sliderParentElement.appendChild(newSlider);
      }
      
      // Re-bind event listeners for the new element
      this.bindSelectButtons();
      
      // Reset any button states for new slider
      setTimeout(() => {
        this.resetGiftButtons();
      }, 100);
    }
    // SECURE: Show existing slider if it should show and exists
    else if (shouldShow && sliderCurrentlyExists) {
      console.log('SECURITY: Showing existing slider - conditions met');
      slider.style.display = 'block';
    }

    console.log('Slider visibility security check:', {
      shouldShow,
      sliderExists: sliderCurrentlyExists,
      hasStoredHTML: !!this.originalSliderHTML,
      giftCount: this.giftCount,
      cartTotal,
      thresholdOne: this.thresholdOne,
      thresholdTwo: this.thresholdTwo,
      changeNeeded: shouldShow !== sliderCurrentlyExists
    });
  }

  async updateSliderContent(cartTotalWithoutGifts) {
    console.log('Updating slider content for cart total:', cartTotalWithoutGifts);
  }

  async checkAndRemoveInvalidGifts(cartData) {
    const cartTotalWithoutGifts = await this.getCartTotalWithoutGifts();
    const items = Array.isArray(cartData?.items) ? cartData.items : [];

    // Separate auto-injected items from user-selected gifts
    const autoInjectItems = items.filter(item => item.properties?._auto_injected === 'true');
    const giftItems = items.filter(item =>
      item.properties?._gift === 'true' &&
      item.properties?._auto_injected !== 'true'
    );

    // Remove all gifts AND auto-inject items if below threshold one
    if (cartTotalWithoutGifts < this.thresholdOne) {
      if (giftItems.length > 0) {
        console.log('Cart below threshold one, removing all user-selected gifts');
        for (const item of giftItems) {
          await this.removeItemFromCart(item.key);
        }
      }
      if (autoInjectItems.length > 0) {
        console.log('Cart below threshold one, removing auto-injected items');
        for (const item of autoInjectItems) {
          await this.removeItemFromCart(item.key);
        }
      }
      this.giftThresholdMap.clear();
      return;
    }

    // Remove threshold two gifts if between thresholds
    if (cartTotalWithoutGifts >= this.thresholdOne && cartTotalWithoutGifts < this.thresholdTwo) {
      const giftsToRemove = giftItems.filter(item =>
        this.giftThresholdMap.get(item.key) === this.thresholdTwo
      );

      if (giftsToRemove.length > 0) {
        console.log('Cart between thresholds, removing threshold two gifts');
        for (const item of giftsToRemove) {
          await this.removeItemFromCart(item.key);
          this.giftThresholdMap.delete(item.key);
        }
      }

      // Limit to one user-selected gift for threshold one (auto-inject doesn't count)
      const remainingGifts = giftItems.filter(item =>
        this.giftThresholdMap.get(item.key) === this.thresholdOne
      );

      if (remainingGifts.length > 1) {
        console.log('Too many threshold one gifts, keeping only one');
        for (let i = 1; i < remainingGifts.length; i++) {
          await this.removeItemFromCart(remainingGifts[i].key);
          this.giftThresholdMap.delete(remainingGifts[i].key);
        }
      }
      return;
    }

    // Limit to 2 user-selected gifts if above threshold two (auto-inject doesn't count)
    if (cartTotalWithoutGifts >= this.thresholdTwo && giftItems.length > 2) {
      console.log('Cart above threshold two, limiting to 2 user-selected gifts');
      const sortedGifts = giftItems.sort((a, b) => {
        const thresholdA = this.giftThresholdMap.get(a.key) || 0;
        const thresholdB = this.giftThresholdMap.get(b.key) || 0;
        return thresholdA - thresholdB;
      });

      for (let i = 2; i < sortedGifts.length; i++) {
        await this.removeItemFromCart(sortedGifts[i].key);
        this.giftThresholdMap.delete(sortedGifts[i].key);
      }
    }
  }

  async checkAndRemoveGiftsOnLoad() {
    const cartData = await this.getCartData();
    this.giftCount = this.countGiftsInCart(cartData);
    this.updateGiftThresholdMap(cartData);
    await this.checkAndRemoveInvalidGifts(cartData);

    // Check auto-inject on page load
    const updatedCartData = await this.getCartData();
    await this.checkAndHandleAutoInject(updatedCartData);

    await this.updateSliderVisibility();
  }

  async removeItemFromCart(key) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!response.ok) throw new Error('Failed to remove item from cart');
      
      const updatedCart = await response.json();
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-slider-manager',
        cartData: updatedCart
      });
      
      return updatedCart;
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  }

  async getCartData() {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { total_price: 0, items: [] };
    }
  }

  bindSelectButtons() {
    const bind = () => {
      const buttons = document.querySelectorAll('.cart-gift-product__select-button');
      buttons.forEach(button => {
        button.removeEventListener('click', this.handleSelectGift);
        button.addEventListener('click', this.handleSelectGift.bind(this));
      });
    };

  this.handleSelectGift = async (event) => {
  const button = event.currentTarget;
  // Prevent double-click
  if (button.classList.contains('adding-gift')) return;

  // Disable all buttons so nothing else can fire
  const allButtons = document.querySelectorAll('.cart-gift-product__select-button');
  allButtons.forEach(btn => {
    btn.disabled = true;
    btn.classList.remove('adding-gift');
    // reset text just in case
    btn.textContent = 'Select Gift';
  });

  // Only the clicked one shows "Adding…"
  button.classList.add('adding-gift');
  button.textContent = 'Adding…';

  try {
    const variantId = button.dataset.giftVariantId;
    const cartData = await this.getCartData();
    this.giftCount = this.countGiftsInCart(cartData);
    const cartTotalWithoutGifts = await this.getCartTotalWithoutGifts();

    // your existing threshold checks…
    if (cartTotalWithoutGifts < this.thresholdOne) throw new Error('Below threshold one');
    if (cartTotalWithoutGifts < this.thresholdTwo && this.giftCount >= 1) throw new Error('Only one gift allowed');
    if (cartTotalWithoutGifts >= this.thresholdTwo && this.giftCount >= 2) throw new Error('Only two gifts allowed');

    const threshold = cartTotalWithoutGifts >= this.thresholdTwo && this.giftCount === 1
      ? this.thresholdTwo
      : this.thresholdOne;

    // This is the magic line that actually adds to cart now:
    await this.addGiftToCart(variantId, threshold);

    // then refresh your slider/indicator…
    setTimeout(() => this.updateSliderVisibility(), 100);
  } catch (err) {
    console.error('Gift add blocked or failed:', err);
  } finally {
    // restore all buttons
    setTimeout(() => {
      allButtons.forEach(btn => {
        btn.disabled = false;
        btn.classList.remove('adding-gift');
        btn.textContent = 'Select Gift';
      });
    }, 500);
  }
};

    bind();
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => bind());
  }

  resetGiftButtons() {
    const allGiftButtons = document.querySelectorAll('.cart-gift-product__select-button');
    allGiftButtons.forEach(btn => {
      btn.disabled = false;
      btn.classList.remove('adding-gift');
      btn.textContent = 'Select Gift';
    });
  }

  async addGiftToCart(variantId, threshold) {
    try {
      console.log('Adding gift to cart:', variantId, 'for threshold:', threshold);
      
      const cartData = await this.getCartData();
      const cartTotalWithoutGifts = await this.getCartTotalWithoutGifts();

      const isOneDollar = window.one_dollar === true || window.one_dollar === 'true' || window.giftPromo?.oneDollar === true || window.giftPromo?.oneDollar === 'true';
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            quantity: 1,
            id: variantId,
            properties: {
              '_gift': 'true',
              '_threshold': threshold,
              '_one_dollar': isOneDollar ? 'true' : 'false'
            }
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to add gift to cart');
      
      const result = await response.json();
      console.log('Gift added successfully:', result);

      const updatedCartData = await this.getCartData();
      this.giftCount = this.countGiftsInCart(updatedCartData);
      this.updateGiftThresholdMap(updatedCartData);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'gift-slider-manager',
        cartData: updatedCartData
      });

      // Update slider visibility based on new state
      await this.updateSliderVisibility();

      if (window.cartThresholdIndicator) {
        window.cartThresholdIndicator.giftCount = this.giftCount;
        window.cartThresholdIndicator.updateProgressBar({
          total_price: cartTotalWithoutGifts,
          items: updatedCartData.items
        });
      }

      return updatedCartData;
    } catch (error) {
      console.error('Error adding gift to cart:', error);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.giftSliderManager = new GiftSliderManager();
});

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');
    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);
    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate(event.cartData);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';
    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }
    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }
async onCartUpdate(cartData) {
    try {
        // Check if there are any invalid gifts before updating DOM
        if (window.giftSliderManager) {
            const cartTotalWithoutGifts = await window.giftSliderManager.getCartTotalWithoutGifts();
            const hasInvalidGifts = cartData.items.some(item => {
                if (item.properties?._gift === 'true') {
                    const threshold = Number(item.properties._threshold);
                    if (cartTotalWithoutGifts < window.thresholdOne) return true;
                    if (threshold === window.thresholdTwo && cartTotalWithoutGifts < window.thresholdTwo) return true;
                }
                return false;
            });
            
            // If invalid gifts exist, don't update the DOM yet
            if (hasInvalidGifts) {
                console.log('Invalid gifts detected, waiting for cleanup...');
                return;
            }
        }
        
        const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
                targetElement.replaceWith(sourceElement);
            }
        }
        
        // Restore icon positions after DOM update
        setTimeout(() => {
            const indicator = document.querySelector('cart-threshold-indicator');
            if (indicator && indicator.restoreIconPositions) {
                indicator.restoreIconPositions();
            }
        }, 0);
    } catch (e) {
        console.error('Error updating cart:', e);
    }
}
  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    fetch(`${routes.cart_change_url}`, {
      ...fetchConfig(),
      ...{ body }
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) ||
            document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');
          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }
          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');
          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);
          const lineItem =
            document.getElementById(`CartItem-${line}`) ||
            document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
          if (!parsedState.errors) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
          }
        });
        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors =
          document.getElementById('cart-errors') ||
          document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) ||
      document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);
    const cartStatus =
      document.getElementById('cart-live-region-text') ||
      document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems =
      document.getElementById('main-cart-items') ||
      document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');
    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);
    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();
        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, {
              ...fetchConfig(),
              ...{ body }
            })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}